<template>
  <header class="tw-mb-11">
    <h2 class="tw-text-[1.7rem] tw-font-bold">{{ t('auth.titles.mfa_setup').toUpperCase() }}</h2>
    <p v-if="!mfaMethodConfirmed" class="tw-text-base tw-font-light">
      {{ t('auth.forms.choose_mfa') }}
    </p>
    <p
      v-else-if="mfaMethodConfirmed && selectedMfaMethod === 'qrCode'"
      class="tw-text-base tw-font-light"
    >
      {{ t('auth.titles.scan_qr') }}
    </p>
    <p
      v-else-if="mfaMethodConfirmed && selectedMfaMethod === 'sms'"
      class="tw-text-base tw-font-light"
    >
      {{ t('auth.titles.enter_phone') }}
    </p>
  </header>

  <div
    v-if="errorMessage"
    class="tw-bg-red-100 tw-border tw-border-red-400 tw-text-red-700 tw-px-4 tw-py-3 tw-rounded tw-mb-4"
    role="alert"
  >
    {{ errorMessage }}
  </div>

  <div v-if="!mfaMethodConfirmed">
    <div class="tw-flex tw-items-center tw-gap-2 tw-mb-8">
      <input
        class="tw-w-4 tw-h-4 tw-accent-[#38678c]"
        type="radio"
        name="mfaSetup"
        id="mfa_qrCode"
        value="qrCode"
        v-model="selectedMfaMethod"
      />
      <label for="mfa_qrCode">
        {{ t('auth.titles.qr_code') }}
      </label>
    </div>

    <div class="tw-flex tw-items-center tw-gap-2 tw-mb-8">
      <input
        class="tw-w-4 tw-h-4 tw-accent-[#38678c]"
        type="radio"
        name="mfaSetup"
        id="mfa_sms"
        value="sms"
        v-model="selectedMfaMethod"
      />
      <label for="mfa_sms">
        {{ t('auth.titles.send_sms') }}
      </label>
    </div>

    <button
      class="tw-w-full tw-bg-cta-primary tw-text-white tw-rounded-lg tw-min-h-[2.4rem] disabled:tw-opacity-70"
      @click="selectMethod"
    >
      {{ t('common.forms.next') }}
    </button>
  </div>

  <div v-else-if="mfaMethodConfirmed && selectedMfaMethod === 'qrCode'">
    <div class="tw-text-center">
      <VueQrcode
        :value="qrCodeUrl"
        :options="{
          width: 250,
          height: 250,
          color: {
            dark: '#38678c',
          },
        }"
      />
    </div>

    <Form class="tw-flex tw-flex-col" v-slot="{ errors, isSubmitting }" @submit="setupQrCodeMfa">
      <div class="tw-mb-4">
        <label for="code" class="tw-text-[0.9rem] tw-mb-1 tw-block">
          {{ t('auth.titles.code') }}
        </label>

        <Field
          id="code"
          name="code"
          type="text"
          class="tw-w-full tw-border tw-border-[#d8d8d8] tw-rounded-lg tw-text-[0.9rem] tw-px-3 tw-py-1.5 placeholder:tw-text-[#dcdcdc] placeholder:tw-text-[0.9rem] focus:tw-outline-none"
          :class="{ 'tw-border-danger': errors.code }"
          :placeholder="t('auth.titles.enter_code')"
          :rules="codeRules"
        />

        <div v-if="errors.code" class="tw-text-danger tw-text-xs tw-mt-1">{{ errors.code }}</div>
      </div>

      <button
        class="tw-w-full tw-bg-cta-primary tw-text-white tw-rounded-lg tw-min-h-[2.4rem] disabled:tw-opacity-70"
        :disabled="isSubmitting"
      >
        {{ t('auth.titles.login') }}
      </button>
    </Form>
  </div>

  <div v-else-if="mfaMethodConfirmed && selectedMfaMethod === 'sms'">
    <Form
      class="tw-flex tw-flex-col"
      v-slot="{ errors, isSubmitting }"
      @submit="setupMobileNumberMfa"
    >
      <div class="tw-mb-4">
        <label for="mobileNumber" class="tw-text-[0.9rem] tw-mb-1 tw-block">
          {{ t('auth.titles.phone_number') }}
        </label>

        <Field
          id="mobileNumber"
          name="mobileNumber"
          type="text"
          class="tw-w-full tw-border tw-border-[#d8d8d8] tw-rounded-lg tw-text-[0.9rem] tw-px-3 tw-py-1.5 placeholder:tw-text-[#dcdcdc] placeholder:tw-text-[0.9rem] focus:tw-outline-none"
          :class="{ 'tw-border-danger': errors.mobileNumber }"
          :placeholder="t('auth.titles.enter_phone')"
          :rules="mobileNumberRules"
        />

        <div v-if="errors.mobileNumber" class="tw-text-danger tw-text-xs tw-mt-1">
          {{ errors.mobileNumber }}
        </div>
      </div>

      <button
        class="tw-w-full tw-bg-cta-primary tw-text-white tw-rounded-lg tw-min-h-[2.4rem] disabled:tw-opacity-70"
        :disabled="isSubmitting"
      >
        {{ t('common.forms.verify') }}
      </button>
    </Form>
  </div>
</template>

<script setup lang="ts">
import { useAuthStore } from '@/stores/auth.store';
import { storeToRefs } from 'pinia';
import { ref } from 'vue';

import VueQrcode from '@chenfengyuan/vue-qrcode';
import parseMobile from 'libphonenumber-js/mobile';

import { Form, Field } from 'vee-validate';
import { string } from 'yup';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const codeRules = string()
  .required(t('auth.titles.code_required'))
  .matches(/^\d{6}$/, t('auth.titles.code_invalid'));

const mobileNumberRules = string()
  .required(t('auth.titles.phone_required'))
  .test('validate-mobile-number', t('auth.titles.phone_invalid'), value => {
    if (value) {
      return (parseMobile(value as string) && parseMobile(value as string)?.isValid()) as boolean;
    }

    return false;
  });

const emit = defineEmits<{
  (e: 'nextChallenge', nextChallengeType: string): void;
}>();

const { errorMessage, qrCodeUrl, nextChallenge } = storeToRefs(useAuthStore());
const { setupMfaMethod } = useAuthStore();

const selectedMfaMethod = ref('');
const mfaMethodConfirmed = ref(false);

const selectMethod = () => {
  if (selectedMfaMethod.value) {
    mfaMethodConfirmed.value = true;
  }
};

const setupQrCodeMfa = async (values: any) => {
  await setupMfaMethod('TOTP', { code: values.code });
};

const setupMobileNumberMfa = async (values: any) => {
  await setupMfaMethod('SMS', { mobileNumber: values.mobileNumber });

  emit('nextChallenge', nextChallenge.value);
};
</script>

<style scoped>
/* Using Tailwind classes - no additional styles needed */
</style>
